
import { defineComponent } from "vue";
import ToolTip from "@/components/common/ToolTip.vue";
import { required, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import validateSsn from "@/utils/validations/validateSsn";
import validateDob from "@/utils/validations/validateDob";
import { vMaska } from "maska";
import messages from "@/config/messages";
import { mapMutations, mapGetters } from "vuex";
import common from "@/config/common";
import helper from "@/utils/helper";
import * as apiClient from "@/apiclient/";

interface iUserData {
  customerParticipantId: number;
  firstName: string;
  lastName: string;
  ssn: string;
  dob: string;
  gender?: number | null;
  genderId?: number | null;
  relationship?: number | null;
  relationshipId?: number | null;
  electionDependentId?: number;
  electionId?: number;
}
export default defineComponent({
  directives: { maska: vMaska },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      user: {
        firstName: "",
        lastName: "",
        ssn: "",
        dob: "",
        gender: null,
        relationship: null,
      } as iUserData,
      userFromApi: [] as apiClient.DependentResponseDto[],
      submitted: false,
      showRecaptcha: true,
      loadingTimeout: 30000,
      isFormInvalid: false,
      validationMsg: "",
      isSubmitDisabled: false,
      isResponseValid: false,
      relationShipData: [] as apiClient.RelationshipDto[],
      genderData: [] as apiClient.GenderDto[],
      messages,
      dataLoaded: false,
      relationshipId: 0,
      actions: { add: "add", edit: "edit" },
    };
  },
  validations() {
    return {
      user: {
        firstName: { required },
        lastName: { required },
        ssn: { required, validateSsn },
        dob: { required, validateDob },
        gender: { required },
        relationship: {
          required,
          checkBenefit: helpers.withParams(
            { type: "checkBenefit" },
            (value: number) => {
              if (
                this.benefits?.length &&
                value !== this.relationshipId &&
                (this.relationshipId === common.dependentRelationsId.spouse ||
                  value === common.dependentRelationsId.spouse)
              ) {
                return false;
              }
              return true;
            }
          ),
        },
      },
    };
  },
  components: {
    ToolTip,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    electionDependentId: Number,
    electionId: Number,
    action: String,
    benefits: Array,
  },
  watch: {
    visible(value) {
      if (value) this.getDependent();
    },
  },
  async created() {
    await this.getRelationship();
    await this.getGender();
    for (let field in this.user) {
      this.$watch(
        () => this.v$.user[field]?.$dirty,
        (newDirty) => {
          if (newDirty) {
            this.setFormDirty(true);
          }
        }
      );
    }
    window.addEventListener("beforeunload", this.confirmBeforeUnload);
  },
  beforeUnmount() {
    // Remove the beforeunload event handler when the component is destroyed
    window.removeEventListener("beforeunload", this.confirmBeforeUnload);
  },
  computed: {
    ...mapGetters(["getFormDirty", "getEnrollment"]),
    modalTitle() {
      return this.action === this.actions.add
        ? "Add Dependent"
        : "Modify Dependent Details";
    },
    saveBtnName() {
      return this.action === this.actions.add ? "ADD" : "SAVE";
    },
  },
  methods: {
    ...mapMutations(["setFormDirty"]),
    confirmBeforeUnload(event: any) {
      helper.confirmBeforeUnload(event);
    },
    buttonDesign(): string {
      const buttonStatus = this.v$.user.$invalid;
      return buttonStatus == true ? "btn-secondary" : "btn-primary";
    },
    hideErrors(field: string): void {
      this.v$.user[field].$touch();
      this.isFormInvalid = false;
    },
    closeModal() {
      this.user.firstName = "";
      this.user.lastName = "";
      this.user.ssn = "";
      this.user.dob = "";
      this.user.gender = null;
      this.user.relationship = null;
      this.validationMsg = "";
      this.isFormInvalid = false;
      this.setFormDirty(false);
      this.v$.user.$reset();
      this.$emit("close");
    },
    async getDependent(): Promise<void> {
      if (!this.electionDependentId) {
        return;
      }
      const params = {
        electionDependentId: this.electionDependentId,
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .dependentController()
        .getApiDependent(params);
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.user.firstName = result.data.firstName!;
        this.user.lastName = result.data.lastName!;
        this.user.ssn = result.data.ssn!;
        this.user.relationship = result.data.relationshipId;
        this.relationshipId = result.data.relationshipId!;
        this.user.gender = result.data.genderId;
        this.user.dob = result.data.dob!;
        this.user.dob = helper.formatDate(this.user.dob, "YYYY-MM-DD");
        this.isFormInvalid = false;
        this.isResponseValid = true;
      }
    },
    async saveDependent(): Promise<void> {
      const params: iUserData = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
        relationshipId: this.user.relationship,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        ssn: this.user.ssn,
        dob: this.user.dob,
        genderId: this.user.gender,
      };
      if (this.electionId) {
        params.electionId = this.electionId;
      }
      if (this.electionDependentId) {
        params.electionDependentId = this.electionDependentId;
      }

      this.isSubmitDisabled = true;
      let result;
      if (this.action === this.actions.edit) {
        this.$emit("update:user", params);
        result = await apiClient.dependentController().putApiDependent(params);
      } else {
        result = await apiClient.dependentController().postApiDependent(params);
      }
      this.isSubmitDisabled = false;
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.isFormInvalid = false;
        this.isResponseValid = true;
        this.$emit("getDependents");
        this.closeModal();
      }
    },
    async getRelationship(): Promise<void> {
      const result = await apiClient.userController().getApiUserRelationship();
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.relationShipData = result.data;
        this.isFormInvalid = false;
        this.isResponseValid = true;
      }
    },
    async getGender(): Promise<void> {
      let genderData = await helper.getGenders();
      if (genderData.validationMsg.length > 0) {
        this.isFormInvalid = true;
        this.validationMsg = genderData.validationMsg;
      } else {
        this.genderData = genderData.genderOptions;
        this.isFormInvalid = false;
        this.isResponseValid = true;
      }
    },
  },
});
